import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SITUACAO_LABEL, SITUACAO_TOM } from '../constants';
import { SituacaoRegra } from '../types';
import { useNcmCatalogo } from '../useNcmCatalogo';
import { Chip } from '../style';
import { AdoptButton, Caret, Hint, Node, NodeRight, SectionTitle, Tree } from './style';

interface Props {
  termo: string;
  situacaoPorNcm: Map<string, SituacaoRegra>;
  // O catálogo não tem linha de posição: a descrição vem do NCM adotado mais antigo dela.
  descricaoPorPosicao: Map<string, string | null>;
  onAbrir: (ncm: NcmDoCatalogo) => void;
}

export interface NcmDoCatalogo {
  id: number;
  ncm: string;
  descricao: string | null;
}

export default function CatalogView({
  termo,
  situacaoPorNcm,
  descricaoPorPosicao,
  onAbrir,
}: Props): React.ReactElement {
  const { sugestoes, resultados, carregando } = useNcmCatalogo(termo);
  const [abertas, setAbertas] = useState<Set<string>>(new Set());

  const capitulosDoTenant = useMemo(
    () => new Set([...situacaoPorNcm.keys()].map((n) => n.slice(0, 2))),
    [situacaoPorNcm],
  );

  // Capítulos que a empresa já usa vêm primeiro; dentro de cada grupo, pela ordem do código.
  const ordenados = useMemo(
    () =>
      [...resultados].sort(
        (a, b) =>
          Number(capitulosDoTenant.has(b.ncm.slice(0, 2))) - Number(capitulosDoTenant.has(a.ncm.slice(0, 2))) ||
          a.ncm.localeCompare(b.ncm),
      ),
    [resultados, capitulosDoTenant],
  );

  // Abre o NCM no painel sem gravar nada: ele entra na lista quando a regra for salva ou quando for usado.
  const acao = (n: NcmDoCatalogo) => {
    const situacao = situacaoPorNcm.get(n.ncm);
    return situacao ? (
      <Chip $tone={SITUACAO_TOM[situacao]}>{SITUACAO_LABEL[situacao]}</Chip>
    ) : (
      <AdoptButton type="button" onClick={() => onAbrir(n)}>
        Cadastrar regra
      </AdoptButton>
    );
  };

  if (termo.trim()) {
    return (
      <Tree>
        <SectionTitle>
          Resultados na tabela oficial<span>{carregando ? 'buscando…' : `${ordenados.length} NCMs`}</span>
        </SectionTitle>
        {ordenados.map((r) => (
          <Node key={r.id} $leaf>
            <span />
            <b>{r.ncm}</b>
            <span>{r.descricao || '—'}</span>
            <NodeRight>{acao(r)}</NodeRight>
          </Node>
        ))}
        {!carregando && ordenados.length === 0 && <Hint>Nada encontrado na tabela oficial.</Hint>}
      </Tree>
    );
  }

  const total = sugestoes.reduce((acc, p) => acc + p.livres.length, 0);
  const alternar = (posicao: string) =>
    setAbertas((atual) => {
      const next = new Set(atual);
      if (next.has(posicao)) next.delete(posicao);
      else next.add(posicao);
      return next;
    });

  return (
    <Tree>
      <SectionTitle>
        Perto do que vocês já usam<span>{total} NCMs nas mesmas posições dos adotados</span>
      </SectionTitle>
      {sugestoes.map((p) => {
        const aberta = abertas.has(p.posicao);
        return (
          <React.Fragment key={p.posicao}>
            <Node $clickable onClick={() => alternar(p.posicao)}>
              <Caret>{aberta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</Caret>
              <b>{p.posicao}</b>
              <span>{descricaoPorPosicao.get(p.posicao) ?? ''}</span>
              <NodeRight>
                <Chip $tone="mute">
                  {p.adotados} adotado{p.adotados > 1 ? 's' : ''}
                </Chip>
                <Chip $tone="info">{p.livres.length} para adotar</Chip>
              </NodeRight>
            </Node>
            {aberta &&
              p.livres.map((l) => (
                <Node key={l.catalogoId} $leaf>
                  <span />
                  <b>{l.ncm}</b>
                  <span>{l.descricao || '—'}</span>
                  <NodeRight>{acao({ id: l.catalogoId, ncm: l.ncm, descricao: l.descricao })}</NodeRight>
                </Node>
              ))}
          </React.Fragment>
        );
      })}
      <Hint>Para qualquer outro NCM da tabela oficial, busque pelo código ou pela descrição.</Hint>
    </Tree>
  );
}
